import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { maybeRequestAppReview } from '@/lib/appReview';

export interface TutorialStep {
  id: string;
  targetSelector?: string;
  additionalTargetSelectors?: string[]; // Additional elements to spotlight (e.g., navbar buttons)
  targetElement?: HTMLElement | null;
  title: string;
  description: string;
  page: string; // 'track', 'home', 'social'
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: () => void | Promise<void>;
  delay?: number; // Delay before showing this step (in ms)
  pollPosition?: number; // Number of times to re-poll position after layout updates (for animated elements)
  pollInterval?: number; // Interval between position polls in ms (default 500ms)
  customContent?: 'toolbar-icons'; // Custom rendering hint
  tooltipDelay?: number; // Delay before showing the tooltip (in ms) - spotlight will appear first
  maxRetries?: number; // Max number of retries to find element (default 10)
  retryInterval?: number; // Interval between retries in ms (default 100ms)
}

export interface UpdatedMetricInfo {
  metricKey: string;
  metricName: string;
  isBoolean: boolean;
}

interface TutorialState {
  // Per-username completion tracking
  completedUsernames: Set<string>;
  
  // Current tutorial state
  isActive: boolean;
  currentStep: number;
  steps: TutorialStep[];
  
  // Actions
  startTutorial: (username: string, steps: TutorialStep[]) => void;
  nextStep: () => void;
  skipTutorial: () => void;
  completeTutorial: (username: string) => void;
  hasCompletedTutorial: (username: string) => boolean;
  resetTutorial: () => void;
  setTargetElement: (stepId: string, element: HTMLElement | null) => void;
}

export const useTutorialStore = create<TutorialState>()(
  persist(
    (set, get) => ({
      completedUsernames: new Set<string>(),
      isActive: false,
      currentStep: 0,
      steps: [],

      startTutorial: (username: string, steps: TutorialStep[]) => {
        const { hasCompletedTutorial } = get();
        
        // Allow test users to always run tutorial for testing
        const isTestUser = username === 'testfuy' || username === 'ann' || username === 'testddd' || username === 'auser' || username === 'iamguy';
        
        // Don't start if already completed (unless test user)
        if (!isTestUser && hasCompletedTutorial(username)) {
          console.log(`[Tutorial] User ${username} has already completed the tutorial`);
          return;
        }

        console.log(`[Tutorial] Starting tutorial for ${username}${isTestUser ? ' (test user - always allowed)' : ''}`);
        
        set({
          isActive: true,
          currentStep: 0,
          steps,
        });
      },

      nextStep: () => {
        const { currentStep, steps } = get();
        
        if (currentStep < steps.length - 1) {
          set({ currentStep: currentStep + 1 });
        } else {
          // Tutorial completed
          set({ isActive: false });
        }
      },

      skipTutorial: () => {
        set({
          isActive: false,
          currentStep: 0,
          steps: [],
        });
        maybeRequestAppReview();
      },

      completeTutorial: (username: string) => {
        const { completedUsernames } = get();
        const newSet = new Set(completedUsernames);
        newSet.add(username);
        
        set({
          completedUsernames: newSet,
          isActive: false,
          currentStep: 0,
          steps: [],
        });
        
        console.log(`[Tutorial] Marked as completed for user: ${username}`);
        maybeRequestAppReview();
      },

      hasCompletedTutorial: (username: string) => {
        return get().completedUsernames.has(username);
      },

      resetTutorial: () => {
        set({
          isActive: false,
          currentStep: 0,
          steps: [],
        });
      },

      setTargetElement: (stepId: string, element: HTMLElement | null) => {
        const { steps } = get();
        const updatedSteps = steps.map((step) =>
          step.id === stepId ? { ...step, targetElement: element } : step
        );
        set({ steps: updatedSteps });
      },
    }),
    {
      name: 'gitfit-tutorial-storage',
      partialize: (state) => ({
        completedUsernames: Array.from(state.completedUsernames),
      }),
      merge: (persistedState: any, currentState) => ({
        ...currentState,
        completedUsernames: new Set(persistedState?.completedUsernames || []),
        // Always reset tutorial state on load to prevent stale state
        isActive: false,
        currentStep: 0,
        steps: [],
      }),
    }
  )
);

// Type for translation function
type TranslationFunction = (key: string) => string;

// Helper hook to manage tutorial for a specific user
export const useFirstTrackTutorial = (username: string | null, t?: TranslationFunction) => {
  const {
    isActive,
    currentStep,
    steps,
    startTutorial,
    nextStep,
    skipTutorial,
    completeTutorial,
    hasCompletedTutorial,
  } = useTutorialStore();

  const isTestUser = username === 'testfuy' || username === 'ann' || username === 'testddd' || username === 'auser' || username === 'iamguy';
  const shouldShowTutorial = username && (isTestUser || !hasCompletedTutorial(username));
  const currentStepData = steps[currentStep];

  const triggerTutorial = (updatedMetric?: UpdatedMetricInfo) => {
    if (!username) return;
    
    // Log for test user
    if (isTestUser) {
      console.log(`[Tutorial] Test user @${username} - forcing tutorial to run`);
    }

    // Close any open dialogs before starting tutorial
    const closeEvent = new CustomEvent('close-all-dialogs');
    window.dispatchEvent(closeEvent);

    // Navigate to track page
    const navigateEvent = new CustomEvent('navigate-to-page', { detail: { page: 'track' } });
    window.dispatchEvent(navigateEvent);

    // Always select the first chart/metric button at index 0 for spotlight
    const firstMetricSelector = '.quick-metric-buttons-container button:first-of-type';
    const firstChartSelector = '[data-chart-metric]:first-of-type';

    // Helper to get translated text or fallback
    const getText = (key: string, fallback: string) => t ? t(key) : fallback;

    const tutorialSteps: TutorialStep[] = [
      {
        id: 'progress-bar',
        page: 'track',
        targetSelector: firstMetricSelector,
        title: getText('onboarding.integrated_tutorial.track_updated_title', 'Track Updated! 🎉'),
        description: getText('onboarding.integrated_tutorial.track_updated_description', "FYI, you can tap to update manually too."),
        position: 'bottom',
      },
      {
        id: 'contribution-chart',
        page: 'home',
        targetSelector: firstChartSelector,
        additionalTargetSelectors: ['[data-nav-button="home"]'],
        title: getText('onboarding.integrated_tutorial.contribution_chart_title', 'Your Contribution Chart'),
        description: getText('onboarding.integrated_tutorial.contribution_chart_description', "Your long term progress here"),
        position: 'bottom',
        tooltipDelay: 2000, // Delay tooltip by 2s to let spotlight appear first
        maxRetries: 50, // Wait up to 5s for activity charts to load
        retryInterval: 100,
      },
      {
        id: 'ask-ai-data',
        page: 'home',
        targetSelector: '[data-component="data-insights-input"]',
        title: getText('onboarding.integrated_tutorial.ask_ai_title', 'Ask AI About Your Data'),
        description: getText('onboarding.integrated_tutorial.ask_ai_description', 'Ask things like "What were my average calories this week?" \u2014 GitFit understands your data and can run any calculation on demand.'),
        position: 'bottom',
      },
      {
        id: 'ai-widget-designer',
        page: 'home',
        targetSelector: '[data-component="data-insights-input"]',
        title: getText('onboarding.integrated_tutorial.ai_widget_title', 'Design a Personal Widget'),
        description: getText('onboarding.integrated_tutorial.ai_widget_description', 'You can also ask AI to create a custom widget just for you \u2014 like a pie chart, progress dashboard, or any visual you can imagine. Just type what you want!'),
        position: 'bottom',
      },
      {
        id: 'daily-entries-log',
        page: 'home',
        title: getText('onboarding.integrated_tutorial.daily_entries_title', 'Daily Entries Log'),
        description: getText('onboarding.integrated_tutorial.daily_entries_description', "Everything you logged and progress pics are shown when you tap any chart square"),
        position: 'top',
        action: () => {
          // Dispatch custom event to open the modal with fake data
          const today = new Date().toISOString().split('T')[0];
          const event = new CustomEvent('open-tutorial-daily-entries', {
            detail: {
              date: today,
              metricKey: 'calories',
              metricName: getText('metrics.calories', 'Calories'),
              metricEmoji: '🔥',
              metricUnit: 'kcal',
              isBoolean: false,
              isTutorialFake: true,
            }
          });
          console.log('[Tutorial] Dispatching open-tutorial-daily-entries event');
          window.dispatchEvent(event);
        },
        delay: 500,
      },
      {
        id: 'leaderboard',
        page: 'social',
        targetSelector: '[data-component="leaderboard"]',
        additionalTargetSelectors: ['[data-nav-button="social"]'],
        title: getText('onboarding.integrated_tutorial.leaderboard_title', 'Leaderboard & Community'),
        description: getText('onboarding.integrated_tutorial.leaderboard_description', "Built-in metrics like calories, steps, and miles appear on public leaderboards! Compete with the community and check out other users' charts and progress pics."),
        position: 'top',
        action: () => {
          // Scroll to bottom of the page
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        },
      },
      {
        id: 'ai-input-final',
        page: 'track',
        targetSelector: '[data-component="quick-entry-input"]',
        title: getText('onboarding.integrated_tutorial.track_anything_title', 'Track Anything! 🚀'),
        description: getText('onboarding.integrated_tutorial.track_anything_description', "Want to track something new? Just ask! Create custom metrics for any habit, goal, or metric you want to monitor."),
        position: 'bottom',
      },
    ];

    startTutorial(username, tutorialSteps);
  };

  const handleComplete = () => {
    if (username) {
      completeTutorial(username);
    }
  };

  return {
    isActive,
    currentStep: currentStepData,
    stepIndex: currentStep,
    totalSteps: steps.length,
    shouldShowTutorial,
    triggerTutorial: triggerTutorial as (updatedMetric?: UpdatedMetricInfo) => void,
    nextStep,
    skipTutorial,
    completeTutorial: handleComplete,
  };
};
